import { propertyBlacklist } from '@/core/config';
import { execute } from '@/graphql/execute';
import { LayersQuery } from '@/graphql/graphql';
import { useOrganizations } from '@/hooks/useOrganizations';
import { featuresByIdsQuery, updateFeaturesMutation } from '@/queries/feature.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Layer = LayersQuery['layers'][number];

interface MergedProperty {
  key: string;
  value: string;
  isMultiple: boolean;
}

interface BulkEditDrawerProps {
  open: boolean;
  onClose: () => void;
  featureIds: string[];
  layerId: string;
  projectId: string;
  layers: Layer[];
}

export const BulkEditDrawer = ({ open, onClose, featureIds, layerId, projectId, layers }: BulkEditDrawerProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('project');
  const [edits, setEdits] = useState<Record<string, string>>({});

  const layer = layers.find((l) => l.id === layerId);

  const { data, isLoading } = useQuery({
    queryKey: ['features-by-ids', featureIds],
    queryFn: () => execute(featuresByIdsQuery, { ids: featureIds }),
    enabled: open && featureIds.length > 0,
  });

  const features = data?.featuresByIds ?? [];

  // Reset edits when drawer opens/closes or selection changes
  useEffect(() => {
    setEdits({});
  }, [open, featureIds]);

  // Build property aliases from layer ancestry
  const aliases = useMemo(() => {
    if (!layer) return {};

    const getAncestry = (current: Layer | undefined): Layer[] => {
      if (!current?.parentLayerId) return [];
      const parent = layers.find((l) => l.id === current.parentLayerId);
      if (!parent) return [];
      return [parent, ...getAncestry(parent)];
    };

    return [...getAncestry(layer).reverse(), layer]
      .flatMap((l) => l.propertyAliases || [])
      .reduce(
        (acc, alias) => {
          acc[alias.property] = alias.alias;
          return acc;
        },
        {} as Record<string, string>,
      );
  }, [layer, layers]);

  const getPropertyName = (property: string) => aliases[property] || property;

  // Compute merged properties across all features
  const mergedProperties = useMemo<MergedProperty[]>(() => {
    if (features.length === 0) return [];

    // Collect all property keys (union)
    const allKeys = new Set<string>();
    for (const feature of features) {
      const props = (feature.properties as Record<string, unknown>) || {};
      for (const key of Object.keys(props)) {
        if (!propertyBlacklist.includes(key)) {
          allKeys.add(key);
        }
      }
    }

    return Array.from(allKeys).map((key) => {
      const values = features.map((f) => {
        const props = (f.properties as Record<string, unknown>) || {};
        return props[key] !== undefined && props[key] !== null ? String(props[key]) : '';
      });

      const allSame = values.every((v) => v === values[0]);

      return {
        key,
        value: allSame ? values[0] : '',
        isMultiple: !allSame,
      };
    });
  }, [features]);

  const hasChanges = Object.keys(edits).length > 0;

  const updateMutation = useMutation({
    mutationFn: async () => {
      const input = features.map((feature) => ({
        id: feature.id,
        properties: {
          ...(feature.properties as Record<string, unknown>),
          ...edits,
        },
      }));
      return execute(updateFeaturesMutation, { input });
    },
    onMutate: () => {
      return { toastId: toast.loading(t('bulkEdit.updatingFeatures')) };
    },
    onSuccess: (_data, _vars, context) => {
      analytics.capture('Features Bulk Edited', { count: features.length });
      toast.success(t('bulkEdit.featuresUpdated', { count: features.length }), { id: context?.toastId });
      queryClient.invalidateQueries({ queryKey: ['features-paginated', projectId, layerId] });
      queryClient.invalidateQueries({ queryKey: ['features-by-ids'] });
      queryClient.invalidateQueries({ queryKey: ['features', layerId] });
      queryClient.invalidateQueries({ queryKey: ['layer-property-values', layerId] });
      onClose();
    },
    onError: (error: Error, _vars, context) => {
      toast.error(error.message || t('bulkEdit.failedUpdateFeatures'), { id: context?.toastId });
    },
  });

  const _handleSave = () => {
    updateMutation.mutate();
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerContent>
        <DrawerHeader className='w-lg'>
          <DrawerTitle>
            {t('bulkEdit.editFeatures', { count: featureIds.length })}
          </DrawerTitle>
          {features.length > 0 && (
            <p className='text-sm text-muted-foreground'>
              {t('bulkEdit.sgidLabel', { serials: features.map((f) => f.serial).join(', ') })}
            </p>
          )}
        </DrawerHeader>
        <div className='flex-1 overflow-y-auto'>
          <div className='flex flex-col gap-4 px-4 py-2'>
            {isLoading ? (
              <div className='flex items-center gap-2 text-muted-foreground text-sm py-8 justify-center'>
                <Loader2 className='size-4 animate-spin' />
                {t('bulkEdit.loadingProperties')}
              </div>
            ) : features.length === 0 ? (
              <p className='text-muted-foreground text-sm py-8 text-center'>{t('bulkEdit.noFeaturesFound')}</p>
            ) : (
              mergedProperties.map((prop) => {
                const edited = prop.key in edits;
                const displayValue = edited ? edits[prop.key] : prop.value;

                return (
                  <div key={prop.key} className='flex flex-col gap-2'>
                    <Label>{getPropertyName(prop.key)}</Label>
                    <Input
                      placeholder={prop.isMultiple && !edited ? t('bulkEdit.multipleValues') : undefined}
                      value={displayValue}
                      onChange={(e) => {
                        setEdits((prev) => ({ ...prev, [prop.key]: e.target.value }));
                      }}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
        <DrawerFooter>
          <Button variant='default' onClick={_handleSave} disabled={!hasChanges || updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className='size-4 animate-spin' />
                {t('bulkEdit.saving')}
              </>
            ) : (
              t('bulkEdit.save')
            )}
          </Button>
          <Button variant='link' onClick={onClose}>
            {t('bulkEdit.cancel')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
